import { Component, OnInit } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { UserLevel, userLevelIsEnough } from '@app/core/models/user-level';
import { MenuService } from '@app/menu/services/menu.service';
import { RouterService } from '@app/routing/router.service';
import { RouteDetectionService } from '@app/shared/services/route-detection.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'portal-menu-container',
  templateUrl: './menu-container.component.html',
  standalone: false,
})
export class MenuContainerComponent implements OnInit {
  UserLevel = UserLevel;

  authenticated$ = this.authenticationService.isAuthenticated$;
  public isAcd: boolean = this.routeDetectionService.isAcdRoute(location.href);
  public isCallCenter: boolean = this.routeDetectionService.isCallCenterQueueRoute(location.href);
  public isEndUser: boolean = false;
  public isEnterpriseAdmin: boolean = false;
  public isServiceProviderAdmin: boolean = false;
  public isSite: boolean = this.routeDetectionService.isSiteRoute(location.href);
  routeDataIsAvailable$: Observable<boolean>;
  public showEndUserMenu: boolean = false;
  public showEnterpriseMenu: boolean = false;
  userLevel$: Observable<UserLevel>;
  /**
   * Current level the user navigated to
   */
  private userLevel?: UserLevel;

  get showEnterpriseAdminMainNavigation(): boolean {
    const isOnEnterpriseLevel = this.currentLevelIs(UserLevel.EnterpriseAdmin);
    const isOnEndUserLevel = this.currentLevelIs(UserLevel.EndUser);
    const isNotEndUser = this.isEndUser === false;

    return isOnEnterpriseLevel || (isOnEndUserLevel && isNotEndUser);
  }

  get showServiceProviderMainNavigation(): boolean {
    return this.currentLevelIs(UserLevel.ServiceProviderAdmin);
  }

  get showSystemMainNavigation(): boolean {
    return this.currentLevelIs(UserLevel.SystemAdmin);
  }

  constructor(
    private authenticationService: AuthenticationService,
    private menuService: MenuService,
    private routeDetectionService: RouteDetectionService,
    private routerService: RouterService
  ) {
    this.routeDataIsAvailable$ = routerService.routeDataChange$.pipe(
      map((routeData) => {
        return routeData.current.params != null;
      }),
      untilDestroyed(this)
    );

    this.userLevel$ = this.menuService.menuData$.pipe(untilDestroyed(this)).pipe(
      map((menuData) => menuData.userLevel),
      filter((userLevel) => userLevel != null)
    );
  }

  currentLevelIs(userLevel: UserLevel) {
    return this.userLevel === userLevel;
  }

  is$(userLevel: UserLevel) {
    return this.userLevel$.pipe(filter((_) => _ === userLevel));
  }

  ngOnInit() {
    this.authenticationService.authUser$.pipe(untilDestroyed(this)).subscribe(() => {
      this.isEndUser = this.authenticationService.userIsEndUser();
      this.isEnterpriseAdmin = this.authenticationService.userIsEnterpriseAdmin();
      this.isServiceProviderAdmin = this.authenticationService.userIsServiceProviderAdmin();
    });

    this.routerService.routeChanged$
      .pipe(untilDestroyed(this))
      .subscribe((route) => this.updateNavigation(route));

    this.userLevel$.subscribe((userLevel) => {
      if (
        userLevelIsEnough(userLevel, this.authenticationService.authUser) === true ||
        this.authenticationService.userIsSystemAdmin()
      ) {
        this.userLevel = userLevel;

        this.showEnterpriseMenu = this.currentLevelIs(UserLevel.EnterpriseAdmin);
        this.showEndUserMenu = this.currentLevelIs(UserLevel.EndUser);
      }
    });
  }

  private updateNavigation(route): void {
    this.isAcd = this.routeDetectionService.isAcdRoute(route.url);
    this.isCallCenter = this.routeDetectionService.isCallCenterQueueRoute(route.url);
    this.isSite = this.routeDetectionService.isSiteRoute(route.url);

    this.showEnterpriseMenu = this.currentLevelIs(UserLevel.EnterpriseAdmin);
    this.showEndUserMenu = this.currentLevelIs(UserLevel.EndUser);
  }
}
